import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { GridLayout } from 'components/grid/GridLayout';
import { FormCheckbox, FormHelperText } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { NotificationInline } from 'components/notifications';

const defaultObject = {};
const multiplePermissionsSummary = "Multiple selected topics require permissions that not all users will have:";

export function ReviewTypeTopicsStep(
    {
        reviewType,
        onUpdate,
        errors = defaultObject,
        reviewOptions = defaultObject,
        loading = false,
        saving = false
    }
) {

    function onTopicChange(topicId, event) {
        let newTopicIds;

        if (event.currentTarget.checked) {
            newTopicIds = [...reviewType.topicIds, topicId];
        } else {
            newTopicIds = reviewType.topicIds.filter((id) => id !== topicId);
        }

        onUpdate({
            ...reviewType,
            topicIds: newTopicIds
        });
    }

    // The distinct messages of the selected topics that need a permission, so an administrator
    // knows this review type can only be completed by users holding them. Topics that need no
    // permission, and topics left unselected, contribute nothing.
    function getSelectedPermissionMessages() {
        const messages = [];

        (reviewOptions?.topics ?? []).forEach((topic) => {
            const selected = reviewType?.topicIds?.some((id) => id === topic.id) === true;

            if (selected && topic.permissionMessage != null && topic.permissionMessage !== "" && !messages.includes(topic.permissionMessage)) {
                messages.push(topic.permissionMessage);
            }
        });

        return messages;
    }

    // A single requirement reads as a sentence on its own; more than one needs an opening line,
    // otherwise the messages run together as an unexplained list.
    function buildPermissionWarning(messages) {
        if (messages.length === 1) {
            return messages[0];
        }

        return (
            <Fragment>
                <div>{multiplePermissionsSummary}</div>
                <ul>
                    {messages.map((message) => {
                        return <li key={message}>{message}</li>;
                    })}
                </ul>
            </Fragment>
        );
    }

    const selectedPermissionMessages = getSelectedPermissionMessages();

    return (
        <div>
            <h2>Review Topics</h2>
            <FormHelperText loading={loading}>Choose the topics that make up this review.</FormHelperText>
            <div className="spacer-bottom">
                <GridLayout numberOfColumns={3} gap={20}>
                    {(reviewOptions?.topics ?? []).map((topic) => {
                        return (
                            <FormCheckbox
                                key={`review-topic-${topic.id}`}
                                id={`review-topic-${topic.id}`}
                                label={topic.name}
                                checked={reviewType?.topicIds?.some((id) => id === topic.id)}
                                disabled={saving || topic.enabled === false}
                                loading={loading}
                                onChange={(event) => onTopicChange(topic.id, event)}
                            />
                        );
                    })}
                </GridLayout>
                <ShowHide
                    evaluator={errors.topicIds != null}
                    show={(
                        <div className="form-field">
                            <FormHelperText loading={loading} danger={true}>{errors.topicIds}</FormHelperText>
                        </div>
                    )}
                />
            </div>
            <NotificationInline
                intent="warning"
                allowClose={false}
                show={selectedPermissionMessages.length > 0}
                text={buildPermissionWarning(selectedPermissionMessages)}
            />
        </div>
    );
}

ReviewTypeTopicsStep.propTypes = {
    reviewType: PropTypes.object.isRequired,
    onUpdate: PropTypes.func.isRequired,
    errors: PropTypes.object,
    reviewOptions: PropTypes.object,
    loading: PropTypes.bool,
    saving: PropTypes.bool
};
